import React, {PureComponent} from 'react';
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {PageDataContext} from "../../../core/PageDataContext";
import {withRouter} from "../../../hooks/withRouter";
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import Collapse from "@mui/material/Collapse";
import moment from "moment";
import {Line} from "react-chartjs-2";
import {ColorPicker} from "../../../core/colors/ColorPicker";
import Divider from "@mui/material/Divider";
import InsightsIcon from "@mui/icons-material/Insights";
import GoalCard from "../../routine/partials/Goals/GoalCard";
import ExerciseNewOrEditFormPopUp from "../partials/ExerciseNewOrEditFormPopUp";
import {ExerciseStatsCalculator} from "../../../core/exercises/ExerciseStatsCalculator";

class ExerciseDetail extends PureComponent {
	constructor(props) {
		super(props);

		const exerciseId = this.props.params.exerciseId;
		const cacheKey = UrlBuilder.exercise.exercisesApi(exerciseId);

		this.state = {
			exerciseId: exerciseId,
			exercise: UserLocalStorage.get(cacheKey) ?? null,
			isLoading: false,
			isEditPopupOpen: false,
			activeChart: 'volume',
		};
	}

	componentDidMount() {
		this.fetchDetail();
	}

	hasNeverBeenFetched = () => this.state.exercise === null;

	fetchDetail = async () => {
		this.setState({isLoading: true});

		const response = await fetch(UrlBuilder.exercise.exercisesApi(this.state.exerciseId));
		if (response.status !== 200) {
			this.setState({isLoading: false});
			return;
		}

		const data = await response.json();
		this.setState({exercise: data, isLoading: false});
		this.context.setPageData({title: data.name});

		UserLocalStorage.set(UrlBuilder.exercise.exercisesApi(this.state.exerciseId), data);
	}

	refresh = () => {
		UserLocalStorage.remove(UrlBuilder.exercise.exercisesApi(this.state.exerciseId));
		this.fetchDetail();
	}

	getExerciseColor = () => {
		const e = this.state.exercise;
		return e?.color ?? ColorPicker.pick(e?.name ?? '');
	}

	// --- Section Header helper ---
	renderSectionHeader = (icon, title) => (
		<Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, mt: 0.5}}>
			<Box sx={{
				width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
			}}>
				{React.cloneElement(icon, {sx: {fontSize: 20, color: 'text.secondary'}})}
			</Box>
			<Typography variant="h6" sx={{fontWeight: 700, fontSize: 18}}>
				{title}
			</Typography>
		</Box>
	)

	// --- Header ---
	renderHeader = () => {
		const e = this.state.exercise;
		const color = this.getExerciseColor();

		return (
			<Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 2}}>
				<IconButton onClick={() => this.props.navigate(UrlBuilder.exercise.exercisesOverviewPage())} size="small">
					<ArrowBackIcon />
				</IconButton>
				<Box sx={{
					width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
					bgcolor: color,
					display: 'flex', alignItems: 'center', justifyContent: 'center',
				}}>
					<Typography sx={{color: 'white', fontWeight: 700, fontSize: 20}}>
						{e.name.charAt(0).toUpperCase()}
					</Typography>
				</Box>
				<Box sx={{flex: 1, minWidth: 0}}>
					<Typography variant="h5" sx={{fontSize: 21, fontWeight: 700, lineHeight: 1.2}} noWrap>
						{e.name}
					</Typography>
					<Box sx={{display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap'}}>
						<Chip label={e.type?.name} size="small" variant="outlined" sx={{height: 22, fontSize: 11}} />
						{e.category && (
							<Chip label={e.category.name} size="small" sx={{height: 22, fontSize: 11, bgcolor: 'action.hover'}} />
						)}
					</Box>
				</Box>
				<IconButton onClick={() => this.setState({isEditPopupOpen: true})} size="small">
					<EditIcon fontSize="small" />
				</IconButton>
			</Box>
		);
	}

	// --- Stats Summary ---
	renderStatsSummary = () => {
		const e = this.state.exercise;
		const patterns = ExerciseStatsCalculator.getTrainingPatterns(e.logGroups);

		const totalVolume = (e.logGroups || []).reduce((sum, g) => sum + (g.totalVolume || 0), 0);

		const stats = [
			{label: 'Sessions', value: e.totalSessions},
			{label: 'Total Sets', value: e.totalSets},
		];

		if (totalVolume > 0) {
			stats.push({label: 'Total Volume', value: totalVolume >= 1000 ? Math.round(totalVolume / 1000) + 'k' : Math.round(totalVolume)});
		}

		if (patterns?.sessionsPerMonth) {
			stats.push({label: '/ Month', value: patterns.sessionsPerMonth});
		} else {
			stats.push({label: 'First Logged', value: e.dateFirstLogged ? moment(e.dateFirstLogged).format('MMM YY') : '—'});
		}

		return (
			<Box sx={{
				display: 'grid', gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 1, mb: 3,
			}}>
				{stats.map((stat, i) => (
					<Box key={i} sx={{
						bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
						borderRadius: 2, p: 1.5, textAlign: 'center',
					}}>
						<Typography variant="h6" sx={{fontWeight: 700, fontSize: 18, color: 'secondary.main'}}>
							{stat.value}
						</Typography>
						<Typography variant="caption" sx={{color: 'text.secondary', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5}}>
							{stat.label}
						</Typography>
					</Box>
				))}
			</Box>
		);
	}

	// --- Personal Records ---
	renderPersonalRecords = () => {
		const prs = this.state.exercise.personalRecords;
		if (!prs || prs.length === 0) return null;

		return (
			<Box sx={{mb: 3}}>
				{this.renderSectionHeader(<EmojiEventsIcon />, 'Personal Records')}
				<Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
					{prs.map((pr, i) => (
						<Box key={i} sx={{
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
							bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
							borderRadius: 2, px: 2, py: 1.5,
						}}>
							<Box>
								<Typography variant="body2" sx={{fontWeight: 600}}>
									{pr.attributeName === 'Volume' ? 'Best Volume' :
									 pr.attributeName === '1RM' ? 'One Rep Max' :
									 `Top ${pr.attributeName}`}
								</Typography>
								<Typography variant="caption" sx={{color: 'text.secondary'}}>
									{pr.date ? moment(pr.date).format('MMM D, YYYY') : ''}
								</Typography>
							</Box>
							<Box sx={{textAlign: 'right'}}>
								<Typography variant="h6" sx={{fontWeight: 700, fontSize: 18, color: 'secondary.main'}}>
									{pr.computedValue > 0
										? pr.computedValue.toLocaleString()
										: pr.values.join(' × ')}
								</Typography>
								{pr.values.length > 1 && pr.computedValue === 0 && (
									<Typography variant="caption" sx={{color: 'text.disabled'}}>
										{this.state.exercise.type?.attributes?.map(a => a.name).join(' × ')}
									</Typography>
								)}
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		);
	}

	// --- Progress Charts ---
	renderCharts = () => {
		const logGroups = this.state.exercise.logGroups;
		if (!logGroups || logGroups.length < 2) return null;

		const reversed = [...logGroups].reverse();
		const dates = reversed.map(g => moment(g.date).format('MMM D'));

		const volumeData = reversed.map(g => g.totalVolume || 0);
		const hasVolume = volumeData.some(v => v > 0);

		const topWeightData = reversed.map(g => {
			if (g.topWeightLogIdx != null && g.logs[g.topWeightLogIdx]) {
				return g.logs[g.topWeightLogIdx].values[0];
			}
			return null;
		});
		const hasWeight = topWeightData.some(v => v != null);

		const topRepsData = reversed.map(g => {
			if (g.topRepsLogIdx != null && g.logs[g.topRepsLogIdx]) {
				const log = g.logs[g.topRepsLogIdx];
				return log.values.length > 1 ? log.values[1] : log.values[0];
			}
			return null;
		});
		const hasReps = topRepsData.some(v => v != null);

		// Estimated 1RM (Weight & Reps only)
		const e1rmSessions = ExerciseStatsCalculator.getEstimated1RMPerSession(logGroups);
		const e1rmData = e1rmSessions.length >= 2 ? e1rmSessions.reverse().map(s => s.value) : null;
		const e1rmDates = e1rmData ? e1rmSessions.map(s => moment(s.date).format('MMM D')) : null;

		const chartTabs = [];
		if (e1rmData) chartTabs.push({key: 'e1rm', label: 'Est. 1RM'});
		if (hasVolume) chartTabs.push({key: 'volume', label: 'Volume'});
		if (hasWeight) chartTabs.push({key: 'weight', label: 'Top Weight'});
		if (hasReps) chartTabs.push({key: 'reps', label: 'Top Reps'});

		if (chartTabs.length === 0) return null;

		const activeChart = chartTabs.find(t => t.key === this.state.activeChart) ? this.state.activeChart : chartTabs[0].key;

		let chartData, chartLabels, chartLabel;
		const color = this.getExerciseColor();

		if (activeChart === 'e1rm') {
			chartData = e1rmData;
			chartLabels = e1rmDates;
			chartLabel = 'Estimated 1RM';
		} else if (activeChart === 'volume') {
			chartData = volumeData;
			chartLabels = dates;
			chartLabel = 'Volume';
		} else if (activeChart === 'weight') {
			chartData = topWeightData;
			chartLabels = dates;
			chartLabel = 'Top Weight';
		} else {
			chartData = topRepsData;
			chartLabels = dates;
			chartLabel = 'Top Reps';
		}

		const data = {
			labels: chartLabels,
			datasets: [{
				label: chartLabel,
				fill: true,
				lineTension: 0.3,
				backgroundColor: color + '20',
				borderColor: color,
				borderWidth: 2,
				pointBackgroundColor: color,
				pointBorderColor: color,
				pointRadius: 2,
				pointHitRadius: 10,
				data: chartData,
			}],
		};

		const options = {
			legend: {display: false},
			scales: {
				yAxes: [{ticks: {beginAtZero: false}, gridLines: {display: false}}],
				xAxes: [{ticks: {maxTicksLimit: 6, fontSize: 10}, gridLines: {display: false}}],
			},
			maintainAspectRatio: true,
		};

		return (
			<Box sx={{mb: 3}}>
				{this.renderSectionHeader(<BarChartIcon />, 'Progress')}

				{chartTabs.length > 1 && (
					<Box sx={{display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap'}}>
						{chartTabs.map(tab => (
							<Chip
								key={tab.key}
								label={tab.label}
								size="small"
								variant={activeChart === tab.key ? 'filled' : 'outlined'}
								color={activeChart === tab.key ? 'secondary' : 'default'}
								onClick={() => this.setState({activeChart: tab.key})}
								sx={{fontSize: 12, fontWeight: 600}}
							/>
						))}
					</Box>
				)}

				<Box sx={{
					bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
					borderRadius: 2.5, p: 2,
				}}>
					<Line data={data} options={options} />
				</Box>
			</Box>
		);
	}

	// --- Training Patterns ---
	renderTrainingPatterns = () => {
		const patterns = ExerciseStatsCalculator.getTrainingPatterns(this.state.exercise.logGroups);
		if (!patterns) return null;

		const rows = [];

		if (patterns.avgSetsPerSession > 0) {
			rows.push({label: 'Avg Sets / Session', value: patterns.avgSetsPerSession});
		}
		if (patterns.sessionsPerMonth) {
			rows.push({label: 'Sessions / Month', value: patterns.sessionsPerMonth});
		}
		if (patterns.repRange && patterns.repRange.low !== patterns.repRange.high) {
			rows.push({label: 'Typical Rep Range', value: `${patterns.repRange.low}–${patterns.repRange.high}`});
		}
		if (patterns.mostUsedWeight != null) {
			rows.push({label: 'Most Used Weight', value: patterns.mostUsedWeight});
		}
		if (patterns.avgRpe != null) {
			rows.push({label: 'Average RPE', value: patterns.avgRpe});
		}
		if (patterns.avgRir != null) {
			rows.push({label: 'Average RIR', value: patterns.avgRir});
		}

		if (rows.length === 0) return null;

		return (
			<Box sx={{mb: 3}}>
				{this.renderSectionHeader(<InsightsIcon />, 'Training Patterns')}
				<Box sx={{
					bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
					borderRadius: 2, px: 2, py: 0.5,
				}}>
					{rows.map((row, i) => (
						<React.Fragment key={i}>
							{i > 0 && <Divider />}
							<Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1}}>
								<Typography variant="body2" sx={{fontWeight: 500}}>
									{row.label}
								</Typography>
								<Typography variant="subtitle1" sx={{fontWeight: 700}}>
									{row.value}
								</Typography>
							</Box>
						</React.Fragment>
					))}
				</Box>
			</Box>
		);
	}

	// --- Muscle Breakdown ---
	renderMuscles = () => {
		const muscles = this.state.exercise.muscles;
		if (!muscles || muscles.length === 0) return null;

		return (
			<Box sx={{mb: 3}}>
				{this.renderSectionHeader(<AccessibilityNewIcon />, 'Muscle Breakdown')}
				<Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
					{muscles.map((m, i) => (
						<Box key={i} sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
							<Typography variant="body2" sx={{fontWeight: 500, minWidth: 90}}>
								{m.muscle?.name ?? m.name}
							</Typography>
							<Box sx={{flex: 1, position: 'relative', height: 8, borderRadius: 4, bgcolor: 'action.hover'}}>
								<Box sx={{
									position: 'absolute', left: 0, top: 0, bottom: 0,
									width: `${m.percentage}%`,
									borderRadius: 4,
									bgcolor: this.getExerciseColor(),
									transition: 'width 0.4s ease',
								}} />
							</Box>
							<Typography variant="caption" sx={{color: 'text.secondary', fontWeight: 600, minWidth: 32, textAlign: 'right'}}>
								{m.percentage}%
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
		);
	}

	// --- Goals ---
	renderGoals = () => {
		const goals = this.state.exercise.goals;
		if (!goals || goals.length === 0) return null;

		return (
			<Box sx={{mb: 3}}>
				{this.renderSectionHeader(<TrackChangesIcon />, 'Goals')}
				<Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
					{goals.map(goal => (
						<GoalCard key={goal.id} goal={goal} />
					))}
				</Box>
			</Box>
		);
	}

	// --- Routines ---
	renderRoutines = () => {
		const routines = this.state.exercise.routines;
		if (!routines || routines.length === 0) return null;

		return (
			<Box sx={{mb: 3}}>
				{this.renderSectionHeader(<FitnessCenterIcon />, 'Used In Routines')}
				<Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
					{routines.map((r, i) => (
						<Chip
							key={i}
							label={r.routineName}
							onClick={() => this.props.navigate(UrlBuilder.routine.routineDetailPage(r.routineId))}
							sx={{
								fontWeight: 600,
								borderLeft: `3px solid ${r.routineColor || this.getExerciseColor()}`,
								opacity: r.isArchived ? 0.5 : 1,
							}}
							variant="outlined"
						/>
					))}
				</Box>
			</Box>
		);
	}

	// --- Loading Skeleton ---
	renderLoadingSkeleton = () => (
		<Box sx={{pt: 2}}>
			<Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 2}}>
				<Skeleton variant="circular" width={44} height={44} />
				<Box sx={{flex: 1}}>
					<Skeleton width="60%" height={28} />
					<Skeleton width="30%" height={18} />
				</Box>
			</Box>
			<Box sx={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 3}}>
				{[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={60} sx={{borderRadius: 2}} />)}
			</Box>
			{[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={60} sx={{borderRadius: 2, mb: 1}} />)}
		</Box>
	)

	render() {
		const e = this.state.exercise;

		return (
			<Box sx={{pt: 2, pb: 4}}>
				<Collapse in={this.state.isLoading}>
					<LinearProgress color="secondary" />
				</Collapse>

				{this.hasNeverBeenFetched() ? (
					this.renderLoadingSkeleton()
				) : e ? (
					<>
						{this.renderHeader()}
						{this.renderStatsSummary()}
						{this.renderPersonalRecords()}
						{this.renderCharts()}
						{this.renderTrainingPatterns()}
						{this.renderMuscles()}
						{this.renderGoals()}
						{this.renderRoutines()}

						<ExerciseNewOrEditFormPopUp
							isOpen={this.state.isEditPopupOpen}
							exercise={e}
							closeSelfFunc={(didUpdate) => {
								this.setState({isEditPopupOpen: false});
								if (didUpdate) this.refresh();
							}}
						/>
					</>
				) : null}
			</Box>
		);
	}
}

ExerciseDetail.contextType = PageDataContext;

export default withRouter(ExerciseDetail);
